// [NEW FILE] Shared "organisation" filters: Business Unit, Department, Location, Cost Centre, Legal Entity.
// Used by every screen that lets a user filter employees (see components/common/OrgFilters.jsx).
// An employee object is expected to carry: businessUnit, departmentId, location, costCenter, legalEntity.

export const NONE = "__none__"; // dropdown value meaning "the field is empty"

export const ORG_FILTER_FIELDS = [
  { key: "businessUnit", label: "Business Unit", all: "All business units", none: "No business unit", list: "businessUnits" },
  { key: "departmentId", label: "Department", all: "All departments", none: "No department", list: "departments" },
  { key: "location", label: "Location", all: "All locations", none: "No location", list: "locations" },
  { key: "costCenter", label: "Cost Centre", all: "All cost centres", none: "No cost centre", list: "costCenters" },
  { key: "legalEntity", label: "Legal Entity", all: "All legal entities", none: "No legal entity", list: "legalEntities" },
];

// [UPDATED] PM-18: each filter holds a LIST of chosen values (several departments, several cost centres ...).
// An empty list means "all". A plain string is still accepted, so older callers keep working.
export const EMPTY_ORG_FILTERS = { businessUnit: [], departmentId: [], location: [], costCenter: [], legalEntity: [] };

// The chosen values of one filter as an array.
export const toList = (v) => (Array.isArray(v) ? v : v ? [v] : []);
// The single chosen value, or "" when nothing or more than one value is chosen (for APIs that take one value).
export const singleValue = (v, { allowNone = false } = {}) => {
  const list = toList(v);
  return list.length === 1 && (allowNone || list[0] !== NONE) ? list[0] : "";
};

export const hasOrgFilters = (filters = {}) => ORG_FILTER_FIELDS.some((f) => toList(filters[f.key]).length > 0);

const clean = (v) => String(v ?? "").trim();
const sortText = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
const distinct = (list, get) => [...new Set(list.map((e) => clean(get(e))).filter((v) => v && v !== "—"))].sort(sortText);

// Builds the dropdown options from a list of employee objects.
export function orgOptions(list = []) {
  const departments = new Map();
  list.forEach((e) => {
    const id = clean(e.departmentId);
    if (id && !departments.has(id)) departments.set(id, clean(e.departmentName) && e.departmentName !== "—" ? e.departmentName : id);
  });
  return {
    businessUnits: distinct(list, (e) => e.businessUnit),
    locations: distinct(list, (e) => e.location),
    costCenters: distinct(list, (e) => e.costCenter),
    legalEntities: distinct(list, (e) => e.legalEntity),
    departments: [...departments].map(([id, name]) => ({ id, name })).sort((a, b) => sortText(a.name, b.name)),
  };
}

// true when the employee passes every chosen filter (AND between filters, OR inside one filter)
export function matchesOrg(emp, filters = {}) {
  if (!hasOrgFilters(filters)) return true;
  const e = emp || {};
  // OR inside one filter: the employee passes when any chosen value fits ("__none__" = field is empty)
  const one = (want, have) => (want === NONE ? !clean(have) : clean(have).toLowerCase() === clean(want).toLowerCase());
  const test = (want, have) => { const list = toList(want); return !list.length || list.some((w) => one(w, have)); };
  return test(filters.businessUnit, e.businessUnit)
    && test(filters.departmentId, e.departmentId)
    && test(filters.location, e.location)
    && test(filters.costCenter, e.costCenter)
    && test(filters.legalEntity, e.legalEntity);
}

// The five attributes of an employee as a plain object (used to merge directory data into other rows).
export const pickOrg = (e = {}) => ({
  businessUnit: e.businessUnit || "", departmentId: e.departmentId || "", departmentName: e.departmentName || "",
  location: e.location || "", costCenter: e.costCenter || "", legalEntity: e.legalEntity || "",
});
